import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { Camera, Loader, Save } from 'lucide-react';
import { updateMe } from '../../../services/auth.api';
import { uploadFile } from '../../../services/upload.api';

/** `2026-09-07T00:00:00.000Z` (or a bare date) → `2026-09-07` for <input type="date">. */
const toDateInputValue = (v) => {
  if (!v) return '';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
};

const field = {
  width: '100%',
  padding: '10px 12px',
  borderRadius: 8,
  border: '1.5px solid #e5e7eb',
  fontSize: 13,
  fontFamily: "'Inter', sans-serif",
  background: '#f9fafb',
  outline: 'none',
  boxSizing: 'border-box',
};

const label = {
  display: 'block',
  fontSize: 11,
  fontWeight: 700,
  color: '#9ca3af',
  textTransform: 'uppercase',
  letterSpacing: 0.5,
  marginBottom: 6,
};

export default function EditProfileTab({ user, loading, onUpdated }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [avatar, setAvatar] = useState(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!user) return;
    setName(user.name || '');
    setEmail(user.email || '');
    setDateOfBirth(toDateInputValue(user.dateOfBirth));
    setAvatar(user.avatar || null);
  }, [user]);

  const handleAvatarPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      setUploadingAvatar(true);
      const url = await uploadFile(file);
      if (url) setAvatar(url);
    } catch (err) {
      toast.error(err?.message || 'Could not upload that photo.');
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const trimmedName = name.trim();
    if (trimmedName.length < 2) {
      toast.error('Name must be at least 2 characters.');
      return;
    }
    try {
      setSaving(true);
      const updated = await updateMe({
        name: trimmedName,
        email: email.trim() || null,
        dateOfBirth: dateOfBirth || null,
        avatar: avatar || null,
      });
      onUpdated(updated);
      toast.success('Profile updated.');
    } catch (err) {
      toast.error(err?.message || 'Could not update your profile.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="prof-card">
        <p style={{ fontSize: 13, color: '#9ca3af', margin: 0 }}>Loading your profile…</p>
      </div>
    );
  }

  return (
    <div className="prof-card">
      <p className="prof-card-label">Edit Profile</p>

      <form onSubmit={handleSave} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {/* Avatar */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <div
            style={{
              width: 72, height: 72, borderRadius: '50%', flexShrink: 0,
              background: avatar ? `#fff url(${avatar}) center/cover no-repeat` : '#eff6ff',
              border: '1.5px solid #e5e7eb',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              position: 'relative', cursor: 'pointer', overflow: 'hidden',
            }}
            onClick={() => fileInputRef.current?.click()}
          >
            {!avatar && !uploadingAvatar && (
              <span style={{ fontSize: 22, fontWeight: 800, color: '#0d6efd' }}>
                {name ? name.trim()[0]?.toUpperCase() : '?'}
              </span>
            )}
            {uploadingAvatar && (
              <div style={{ position: 'absolute', inset: 0, background: 'rgba(255,255,255,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Loader size={18} className="prof-spin" />
              </div>
            )}
          </div>
          <div>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadingAvatar}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '8px 14px', borderRadius: 8, border: '1.5px solid #e5e7eb',
                background: '#fff', fontSize: 12, fontWeight: 600, color: '#374151',
                cursor: uploadingAvatar ? 'not-allowed' : 'pointer', fontFamily: "'Inter', sans-serif",
              }}
            >
              <Camera size={13} /> {uploadingAvatar ? 'Uploading…' : 'Change photo'}
            </button>
            <p style={{ margin: '6px 0 0', fontSize: 11, color: '#9ca3af' }}>JPG, PNG, WEBP, GIF or AVIF.</p>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif,image/avif"
            onChange={handleAvatarPick}
            style={{ display: 'none' }}
          />
        </div>

        <div>
          <label style={label}>Full name</label>
          <input
            style={field}
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={100}
            required
          />
        </div>

        <div>
          <label style={label}>Email</label>
          <input
            style={field}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
          />
        </div>

        <div>
          <label style={label}>Date of birth</label>
          <input
            style={field}
            type="date"
            value={dateOfBirth}
            onChange={(e) => setDateOfBirth(e.target.value)}
            max={toDateInputValue(new Date())}
          />
        </div>

        <div>
          <label style={label}>Phone</label>
          <input style={{ ...field, background: '#f1f5f9', color: '#9ca3af' }} value={user?.phone || ''} disabled />
          <p style={{ margin: '6px 0 0', fontSize: 11, color: '#9ca3af' }}>
            Your phone number is your sign-in identity and can't be changed here.
          </p>
        </div>

        <button
          type="submit"
          disabled={saving || uploadingAvatar}
          style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
            padding: '10px 18px', borderRadius: 8, border: 'none', alignSelf: 'flex-start',
            background: saving ? '#9ca3af' : '#0d6efd', color: '#fff', fontSize: 13, fontWeight: 700,
            cursor: saving ? 'not-allowed' : 'pointer', fontFamily: "'Inter', sans-serif",
          }}
        >
          {saving ? <Loader size={14} className="prof-spin" /> : <Save size={14} />}
          {saving ? 'Saving…' : 'Save changes'}
        </button>
      </form>
    </div>
  );
}
